import { Abi, AbiItem, TypedDataDomain, WalletClient } from 'viem';
import { getP2PContract, IEX_TYPES } from '@sdk/orderExecutor/helpers';
import {
  CreateOrder,
  ContractOrderMethod,
  OrderMethodPayload,
  OrderMethodPayloadForContract,
  OrderType,
} from '@sdk/shared/types/order.types';
import { ExtendedWalletClient } from '@sdk/auth/types';
import { orderSigChain } from '@sdk/orderExecutor/helpers/order';
import { Network } from '@sdk/shared/types/config.types';
import { IxEventType, SdkClient } from '..';

/**
 * Sign an order using EIP-712
 * Uses viem
 */
export async function signOrder(
  sdk: SdkClient,
  order: CreateOrder,
  domain: TypedDataDomain,
): Promise<`0x${string}`> {
  const walletClient = sdk.auth.getWalletClient();
  if (!walletClient?.account) {
    throw new Error('Wallet client account is not set');
  }

  if (walletClient.account.type === 'json-rpc') {
    sdk.notification.notify({
      type: IxEventType.SIGN_JSON_RPC_REQUEST,
      data: {
        message: 'Please sign the order in your wallet',
      },
    });
  }

  return await walletClient.signTypedData({
    account: walletClient.account,
    domain,
    types: IEX_TYPES,
    primaryType: 'CreateOrder',
    message: order,
  });
}

export async function ensureCorrectNetwork({
  networks,
  traderChain,
  merchantChain,
  orderType,
  activeChainId,
}: {
  networks: Network[];
  traderChain: number;
  merchantChain: number;
  orderType: OrderType;
  activeChainId: number;
}) {
  const sigchainId = orderSigChain({ traderChain, merchantChain, orderType });
  const p2pContract = await getP2PContract(sigchainId, networks);

  const isCorrectNetwork = sigchainId === activeChainId;

  // const properChain = isMerchant
  //   ? orderData?.merchantNetwork
  //   : orderData?.traderNetwork || chains.find(c => c.id === prepareArgs.merchantChain);
  // if (!properChain) throw new Error('Network not found while checking network');

  return { sigchainId, p2pContract, isCorrectNetwork };
}

export function filterAbiForSpecificFunc(
  functionName: string,
  length: number,
  abi: Abi,
): AbiItem[] {
  const filteredAbi = abi.filter(
    (item): item is AbiItem =>
      'name' in item && item.name === functionName && item.inputs.length === length,
  );
  return filteredAbi;
}

/**
 * Sign an order method using EIP-712
 * Uses viem
 */
export async function signOrderMethod(
  walletClient: ExtendedWalletClient,
  orderMethod: OrderMethodPayloadForContract,
  domain: TypedDataDomain,
): Promise<`0x${string}`> {
  const types = {
    OrderMethodPayload: [
      { name: 'orderHash', type: 'bytes32' },
      { name: 'method', type: 'uint8' },
      { name: 'expiry', type: 'uint256' },
    ],
  } as const;

  if (!walletClient.account) {
    throw new Error('Wallet client account is not set, signing order');
  }

  return await walletClient.signTypedData({
    account: walletClient.account,
    domain,
    types,
    primaryType: 'OrderMethodPayload',
    message: orderMethod,
  });
}
